import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { memberService } from "@/services";
import type { Member, MemberGroupSummary, MemberStatus } from "@/services/types";
import { totalOutstanding, memberGroupCount } from "@/utils/member";
import { STATUS_BADGE, STATUS_LABEL, MEMBER_STATUSES } from "@/config/members";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { useConfirm } from "@/context/ConfirmContext";
import { canDeleteMember } from "@/config/roles";
import { PageHeader, BreadcrumbItem } from "@/components/feature/PageHeader";
import { FilterBar } from "@/components/feature/FilterBar";
import { DataTable, type SortState, type DataTableColumn } from "@/components/feature/DataTable";
import { Button } from "@/components/base/Button";
import { Select, type SelectOption } from "@/components/base/Select";
import { Badge } from "@/components/base/Badge";
import { Dropdown } from "@/components/base/Dropdown";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { ChangeStatusModal } from "./components/ChangeStatusModal";

interface ViewState {
  search: string;
  status: string;
  dues: string;
  group: string;
  sort: SortState | null;
  page: number;
  pageSize: number;
}

const VIEW_KEY = "kameti.members.view";

function loadView(): ViewState {
  try {
    const raw = sessionStorage.getItem(VIEW_KEY);
    if (raw) return { search: "", status: "", dues: "", group: "", sort: null, page: 1, pageSize: 10, ...JSON.parse(raw) };
  } catch {
    // ignore
  }
  return { search: "", status: "", dues: "", group: "", sort: null, page: 1, pageSize: 10 };
}

function exportRows(rows: Member[]) {
  const header = ["Member Code", "Name", "Is Organizer", "Phone", "City", "Groups", "Outstanding", "Status"];
  const body = rows.map((m) => [
    m.memberCode,
    m.fullName,
    m.isOrganizer ? "Yes" : "No",
    m.primaryPhone,
    m.city,
    memberGroupCount(m),
    totalOutstanding(m),
    m.status,
  ]);
  const csv = [header.join(","), ...body.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `members-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

const statusOptions: SelectOption[] = [
  { value: "", label: "All statuses" },
  ...MEMBER_STATUSES.map((s) => ({ value: s.value, label: s.label })),
];

const duesOptions: SelectOption[] = [
  { value: "", label: "Any balance" },
  { value: "yes", label: "Has outstanding dues" },
  { value: "no", label: "No dues" },
];

export default function MembersPage() {
  const navigate = useNavigate();
  const { isViewer, role } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const canDelete = canDeleteMember(role);

  const [initialView] = useState<ViewState>(loadView);
  const [search, setSearch] = useState(initialView.search);
  const [status, setStatus] = useState(initialView.status);
  const [dues, setDues] = useState(initialView.dues);
  const [group, setGroup] = useState(initialView.group);
  const [tableState, setTableState] = useState<{ sort: SortState | null; page: number; pageSize: number }>({
    sort: initialView.sort,
    page: initialView.page,
    pageSize: initialView.pageSize,
  });

  const [members, setMembers] = useState<Member[] | null>(null);
  const [groups, setGroups] = useState<MemberGroupSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Member[]>([]);
  const [statusTarget, setStatusTarget] = useState<Member[] | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [memberData, groupData] = await Promise.all([memberService.listMembers(), memberService.listGroups()]);
      setMembers(memberData);
      setGroups(groupData);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load members.");
      setMembers([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Persist filters + table state so navigating to a profile and back restores the list.
  useEffect(() => {
    try {
      sessionStorage.setItem(
        VIEW_KEY,
        JSON.stringify({ search, status, dues, group, ...tableState })
      );
    } catch {
      // ignore
    }
  }, [search, status, dues, group, tableState]);

  const activeFilterCount = [search, status, dues, group].filter(Boolean).length;

  const filtered = useMemo(() => {
    if (!members) return null;
    let out = members;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      out = out.filter(
        (m) =>
          m.fullName.toLowerCase().includes(q) ||
          m.memberCode.toLowerCase().includes(q) ||
          m.primaryPhone.includes(q) ||
          m.alternatePhone.includes(q)
      );
    }
    if (status) out = out.filter((m) => m.status === status);
    if (dues === "yes") out = out.filter((m) => totalOutstanding(m) > 0);
    if (dues === "no") out = out.filter((m) => totalOutstanding(m) === 0);
    if (group) out = out.filter((m) => m.enrollments.some((e) => e.groupId === group));
    return out;
  }, [members, search, status, dues, group]);

  const groupOptions: SelectOption[] = [
    { value: "", label: "All groups" },
    ...groups.map((g) => ({ value: g.id, label: g.name })),
  ];

  const handleClear = () => {
    setSearch("");
    setStatus("");
    setDues("");
    setGroup("");
  };

  const handleDelete = async (m: Member) => {
    const ok = await confirm({
      title: "Delete Member",
      message: `Are you sure you want to permanently delete member "${m.fullName}" (${m.memberCode})? This action cannot be undone.`,
      confirmLabel: "Delete Member",
      destructive: true,
      dangerText: "Members with recorded payments or active group enrollments cannot be deleted.",
    });
    if (!ok) return;

    try {
      await memberService.deleteMember(m.id);
      toast.success("Member deleted", `${m.fullName} was permanently removed.`);
      await load();
    } catch (err) {
      toast.error("Could not delete member", err instanceof Error ? err.message : "Please try again.");
    }
  };

  const handleStatusConfirm = async (nextStatus: MemberStatus, reason: string) => {
    if (!statusTarget) return;
    const actor = "Staff User";
    try {
      for (const m of statusTarget) {
        await memberService.updateMemberStatus(m.id, nextStatus, reason, actor);
      }
      toast.success(
        statusTarget.length === 1 ? "Status updated" : `${statusTarget.length} members updated`,
        statusTarget.length === 1
          ? `${statusTarget[0].fullName} is now ${STATUS_LABEL[nextStatus].toLowerCase()}.`
          : `All selected members are now ${STATUS_LABEL[nextStatus].toLowerCase()}.`
      );
      setStatusTarget(null);
      await load();
    } catch (err) {
      toast.error("Could not update status", err instanceof Error ? err.message : "Please try again.");
    }
  };

  const columns: DataTableColumn<Member>[] = [
    {
      id: "memberCode",
      header: "Member Code",
      accessor: (m) => (
        <span className="tnum text-xs font-semibold text-primary-700">{m.memberCode}</span>
      ),
      sortValue: (m) => m.memberCode,
      sortable: true,
    },
    {
      id: "fullName",
      header: "Full Name",
      accessor: (m) => (
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="font-medium text-foreground-900">{m.fullName}</span>
            {m.isOrganizer ? (
              <Badge color="accent" className="text-[10px] px-1.5 py-0">
                Organizer
              </Badge>
            ) : null}
          </div>
          <span className="block text-xs text-foreground-400">{m.guardianName ? `S/o · ${m.guardianName}` : ""}</span>
        </div>
      ),
      sortValue: (m) => m.fullName,
      sortable: true,
    },
    {
      id: "phone",
      header: "Phone",
      accessor: (m) => <span className="tnum">{m.primaryPhone}</span>,
      sortValue: (m) => m.primaryPhone,
      sortable: true,
    },
    {
      id: "city",
      header: "City",
      accessor: (m) => <span className="text-foreground-600">{m.city || "—"}</span>,
      sortValue: (m) => m.city,
      sortable: true,
    },
    {
      id: "groups",
      header: "Groups",
      align: "right",
      accessor: (m) => <span className="tnum font-medium">{memberGroupCount(m)}</span>,
      sortValue: (m) => memberGroupCount(m),
      sortable: true,
      footerValue: (rows) => <span className="tnum">{rows.reduce((s, m) => s + memberGroupCount(m), 0)}</span>,
    },
    {
      id: "outstanding",
      header: "Total Outstanding",
      align: "right",
      accessor: (m) => {
        const amt = totalOutstanding(m);
        return amt > 0 ? (
          <CurrencyDisplay amount={amt} tone="danger" />
        ) : (
          <CurrencyDisplay amount={amt} className="text-foreground-400" />
        );
      },
      sortValue: (m) => totalOutstanding(m),
      sortable: true,
      footerValue: (rows) => <CurrencyDisplay amount={rows.reduce((s, m) => s + totalOutstanding(m), 0)} />,
    },
    {
      id: "accountBalance",
      header: "Advance Balance",
      align: "right",
      accessor: (m) => {
        const bal = m.accountBalance ?? 0;
        return bal > 0 ? (
          <CurrencyDisplay amount={bal} tone="success" />
        ) : (
          <span className="text-foreground-400 tnum">₹0.00</span>
        );
      },
      sortValue: (m) => m.accountBalance ?? 0,
      sortable: true,
      footerValue: (rows) => <CurrencyDisplay amount={rows.reduce((s, m) => s + (m.accountBalance ?? 0), 0)} />,
    },
    {
      id: "status",
      header: "Status",
      accessor: (m) => <Badge color={STATUS_BADGE[m.status]} dot>{STATUS_LABEL[m.status]}</Badge>,
      sortValue: (m) => m.status,
      sortable: true,
    },
    {
      id: "actions",
      header: "",
      align: "right",
      width: "56px",
      hideable: false,
      accessor: (m) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Dropdown
            align="right"
            width="w-52"
            trigger={() => (
              <button type="button" aria-label="Row actions" className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 hover:text-foreground-800">
                <i className="ri-more-2-line text-lg" />
              </button>
            )}
            items={[
              { key: "view", label: "View profile", icon: "ri-user-line", onClick: () => navigate(`/members/${m.id}`) },
              { key: "edit", label: "Edit", icon: "ri-pencil-line", disabled: isViewer, onClick: () => navigate(`/members/${m.id}/edit`) },
              { key: "status", label: "Change status", icon: "ri-flag-line", disabled: isViewer, onClick: () => setStatusTarget([m]) },
              ...(canDelete ? [
                { key: "divider-delete", label: "", divider: true },
                {
                  key: "delete",
                  label: "Delete member",
                  icon: "ri-delete-bin-line",
                  danger: true,
                  onClick: () => handleDelete(m),
                },
              ] : []),
              { key: "divider", label: "", divider: true },
              {
                key: "enrol",
                label: <span className="flex items-center gap-2"><i className="ri-add-circle-line text-base" />Enrol into group<span className="text-xs text-foreground-400">(Phase 2)</span></span>,
                disabled: true,
              },
            ]}
          />
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        breadcrumb={
          <>
            <BreadcrumbItem label="Home" to="/" />
            <BreadcrumbItem label="Members" isLast />
          </>
        }
        title="Members"
        subtitle={members ? `${members.length} total member records` : "Loading members…"}
        actions={
          <Button icon={<i className="ri-add-line" />} disabled={isViewer} onClick={() => navigate("/members/new")}>
            Add Member
          </Button>
        }
      />

      <div className="mb-3">
        <FilterBar
          search={{ value: search, onChange: setSearch, placeholder: "Search name, phone, or member code…", loading: members === null }}
          filters={[
            {
              id: "status",
              control: (
                <Select className="w-44" value={status} onChange={setStatus} options={statusOptions} />
              ),
            },
            {
              id: "dues",
              control: <Select className="w-56" value={dues} onChange={setDues} options={duesOptions} />,
            },
            {
              id: "group",
              control: <Select className="w-56" value={group} onChange={setGroup} options={groupOptions} />,
            },
          ]}
          activeCount={activeFilterCount}
          onClear={handleClear}
          rightActions={
            selected.length > 0 ? (
              <div className="flex flex-wrap items-center gap-2">
                <span className="tnum text-xs text-foreground-600">{selected.length} selected</span>
                <Button size="sm" variant="outline" icon={<i className="ri-download-2-line" />} onClick={() => exportRows(selected)}>
                  Export selected
                </Button>
                <Button size="sm" variant="secondary" icon={<i className="ri-flag-line" />} disabled={isViewer} onClick={() => setStatusTarget(selected)}>
                  Change status
                </Button>
              </div>
            ) : null
          }
        />
      </div>

      <DataTable<Member>
        columns={columns}
        rows={filtered}
        rowKey={(m) => m.id}
        loading={members === null}
        error={error}
        onRetry={load}
        emptyMessage="No members found"
        emptyDescription="Try adjusting your filters, or add a new member."
        selectable
        onSelectionChange={setSelected}
        onRowClick={(m) => navigate(`/members/${m.id}`)}
        showTotals
        totalLabel="Totals"
        exportable
        exportFilename="members"
        initialSort={tableState.sort ?? { id: "memberCode", dir: "asc" }}
        initialPage={tableState.page}
        initialPageSize={tableState.pageSize}
        onViewStateChange={(s) => setTableState(s)}
      />

      <ChangeStatusModal
        open={Boolean(statusTarget)}
        onClose={() => setStatusTarget(null)}
        members={statusTarget ?? []}
        onConfirm={handleStatusConfirm}
        disabled={isViewer}
      />
    </div>
  );
}